import {React, styled, _, classNames, observer, utils, useLocalStore, Fuse, Highlighter, pluralize, useEffect, useMemo, useCallback} from '@api'
import {Button, Card, Icon, Navbar, NavbarDivider, NavbarGroup, Menu, MenuItem as BPMenuItem, Popover, ButtonGroup, AnchorButton, HTMLSelect, Spinner, Switch, PopoverInteractionKind} from '@blueprintjs/core'
import {Popover2, Tooltip2} from '@api'
import {Checkbox, ListItemText, MenuItem, Tooltip, Typography, Chip} from '@material-ui/core'
import {ErrorBoundary, useStores, useVipLocalStore, VipLocalStore} from '@design-board/components'
import {ICatalogFieldConfig, site, ISectionConfig, PredefinedCardViewConfig, shouldIgnoreCatalogField, useCatalog, StoresInstance, ui} from '@stores'
import {ICardViewSettings} from '@stores/catalog'
import {action, autorun, computed, observable, runInAction, toJS} from 'mobx'
import {computedFn} from 'mobx-utils'
import {Fragment, useContext} from 'react'
import * as css from './CardViewOptionsToolbarMenu.css'
import {MyPopover2, PrimaryIconButton, SearchField2} from 'centric-ui-components'

/**
 * Button and surrounding popover for picking fields for cards
 */
export const CardViewOptionsToolbarMenu = observer<{ className?: string }>(function CardViewOptionsToolbarMenu({className}) {
  const {sidebar: {disableToolbarMenus}, isGlobalStore} = useStores(),
    store = useVipLocalStore((ws) => new CardViewToolbarMenuPanelStore(ws)),
    {updateCatalogCardView, onPopoverOpening, strings} = store

  useEffect(() => store.registerEffects(), [])

  if (isGlobalStore) return null

  return (
    <CardViewToolbarMenuPanelStoreContext.Provider value={store}>
      <Popover2
        placement='bottom'
        disabled={disableToolbarMenus}
        interactionKind='click'
        autoFocus={false}
        className={classNames(className, css.popoverWrapper)}
        usePortal
        popoverClassName={classNames(css.popover)}
        onClosed={updateCatalogCardView}
        onOpened={onPopoverOpening}
        minimal
        content={<ErrorBoundary>
          <CardViewToolbarMenuPanel/>
        </ErrorBoundary>}>
        <Button minimal
                text={strings.viewOptions}
                rightIcon='caret-down'/>
      </Popover2>

    </CardViewToolbarMenuPanelStoreContext.Provider>
  )
})

/**
 * Dialog responsible for letting users choose field to display on the cards displayed in the catalog
 *
 * This becomes difficult as customers may have attributes into the 100s that they still want to easily be able to choose and display.
 *
 * Other ancillary options are on this dialog as well such as changing the image source, the image ration, the card size, and other global catalog features
 */
export const CardViewToolbarMenuPanel = observer(function CardViewToolbarMenuPanel() {
  const store = useCardViewToolbarMenuPanelStore()

  const {
    workspaces: {workspaceConfig}
  } = useStores()

  if (!workspaceConfig) return null

  const selectedSection = store.selectedSection ?? (!workspaceConfig.sections.length ? workspaceConfig._id : 'all')

  return (
    <BlueprintCardInPopoverRefactorMe className={css.root}>
      <Header/>
      <div className={css.container}>
        <SelectedSectionFieldOptions/>
      </div>
      <div className={css.container}>
        <FieldOptions key={selectedSection}/>
      </div>
      <Footer/>

    </BlueprintCardInPopoverRefactorMe>)
})

const Header = observer(function Header() {
  const store = useCardViewToolbarMenuPanelStore(),
    {isDirty, searchText, onSearch, onChangeSection, sectionOptions, strings} = store
  const {
      catalog: {
        settings, settings: {cardViewOrderOverride}
      },
      workspaces: {workspaceConfig, workspaceConfig: {sections}}
    } = useStores(),
    selectedSection = store.selectedSection ?? (!sections.length ? workspaceConfig._id : 'all'),
    onToggleShowSelectedFirst = useCallback(() => settings.showSelectedFirst = !settings.showSelectedFirst, [settings])

  return (
    <Navbar className={css.header}>
      <NavbarGroup align='left'>

        {!sections?.length ? <NoneDefaultAllButtons/> : <Fragment>
          <HTMLSelect
            className={css.sectionSelect}
            minimal
            options={sectionOptions}
            value={selectedSection}
            onChange={onChangeSection}
          />
          <PresetViewDropdownMenu/>

          <ButtonGroup className={css.selectDefaultButtons}>
            <NoneDefaultAllButtons/>
          </ButtonGroup>
        </Fragment>}
        <ButtonGroup className={css.resetCustomOrder}>
          <Button minimal text={strings.resetCustomOrder} disabled={!cardViewOrderOverride} onClick={() => settings.cardViewOrderOverride = {}}/>
          <Tooltip2 content='Updating Catalog' disabled={!isDirty}><Button minimal data-visible={isDirty} className={css.updateCatalogCardViewButton} onClick={() => store.updateCatalogCardView()}><Spinner size={20}/></Button></Tooltip2>
        </ButtonGroup>
      </NavbarGroup>
      <NavbarGroup style={{marginLeft: 'auto'}} align='right'>
        <Tooltip2 content={`${strings.showSelectedFirst} \n (${strings.disabledDragging})`}>
          <span>
            <Switch
              checked={settings.showSelectedFirst}
              onChange={onToggleShowSelectedFirst}
              label={strings.showSelectedFirst}
            />
          </span>
        </Tooltip2>

        <SearchField2 className={css.search} active onSearch={onSearch} value={searchText}/>
        {/*{catalog.settings.viewMode !== 'table' && <ImageSourceToolbarButton/>}*/}
      </NavbarGroup>
    </Navbar>
  )
})

const PresetViewDropdownMenu = observer(() => {
  const {customCardViews} = useCardViewToolbarMenuPanelStore()
  return _.isEmpty(customCardViews) ? null :
         <ButtonGroup>
           <Popover2
             interactionKind='click'
             minimal
             position='bottom-left'
             className={css.presetsDropdown}
             content={<Menu>
               <CustomCardViewPresetMenuItems/>
             </Menu>}
           >
             <Button rightIcon='caret-down' minimal text='Presets'/>
           </Popover2>
         </ButtonGroup>
})
const Footer = observer(function Footer() {
  const store = useCardViewToolbarMenuPanelStore(),
    {showAutoSaveInline} = store,
    {
      strings, catalog: {
      dialogs, toggleShowCardLabel, settings, settings: {showNestedFieldsOnTopLevelCard, viewMode, showCardLabels, autoSaveInlineChanges}, toggleAutoSaveInlineChanges, cv: {isNestedFieldAvailable}
    }, vlp,
      export: {availableExports}
    } = useStores()

  return (<Navbar className={css.footer}>
    <ToolbarDialogButtonGroup align='left'>
      {showAutoSaveInline ? <Switch onChange={toggleAutoSaveInlineChanges} checked={autoSaveInlineChanges} label={'Auto-Save Inline Changes'}/> : null}
    </ToolbarDialogButtonGroup>

    {isNestedFieldAvailable && <ToolbarDialogButtonGroup align='left'>
		<Switch onChange={() => settings.showNestedFieldsOnTopLevelCard = showNestedFieldsOnTopLevelCard !== true} checked={showNestedFieldsOnTopLevelCard} label={'Show Nested Fields on Main Card'}/>
	</ToolbarDialogButtonGroup>}

    <ToolbarDialogButtonGroup align='left'>
      <Tooltip2 content={strings.showCardLabel}>
        <Switch
          checked={showCardLabels}
          onChange={toggleShowCardLabel}
          label={strings.showLabel}
        />
      </Tooltip2>
    </ToolbarDialogButtonGroup>

    <ToolbarDialogButtonGroup align='left'>
      {!vlp.isVLPView &&
       (<Tooltip2 content={strings.toggleQRCode}>
         <div>
           <Button
             icon='barcode'
             minimal
             active={dialogs.showQrCodes}
             disabled={viewMode === 'table'}
             onClick={site.touchDevice ? undefined : dialogs.toggleShowQrCodes}
             onTouchEnd={site.touchDevice ? (e) => {
               e.preventDefault()
               e.stopPropagation()
               dialogs.toggleShowQrCodes()
             } : undefined}/>
         </div>
       </Tooltip2>)}

      {/* Moved to context menu or elsewhere
      {false && DEBUG ? <Fragment>
                        <NavbarDivider/>
                        <Tooltip2 content={'Flat Mode'}>
                          <PrimaryIconButton onClick={() => gbSettings.groupView = gbSettings.groupView == 'flat' ? 'card' : 'flat'} active={gbSettings.groupView == 'flat'}><Icon icon='alignment-vertical-center'/></PrimaryIconButton>
                        </Tooltip2>

                        <Tooltip2 content={'Wrap Lines'}>
                          <PrimaryIconButton onClick={() => gbSettings.noWrap = !gbSettings.noWrap} active={!gbSettings.noWrap}><Icon icon='vertical-distribution'/></PrimaryIconButton>
                        </Tooltip2>

                        <Tooltip2 content={'Fixed Width'}>
                          <PrimaryIconButton
                            onClick={() => gbSettings.fixedWidth = !gbSettings.fixedWidth}
                            active={gbSettings.fixedWidth}>
                            <Icon icon='horizontal-distribution'/>
                          </PrimaryIconButton>
                        </Tooltip2>

                        <Tooltip2 content={'Show Expanded Items First'}>
                          <PrimaryIconButton
                            onClick={() => gbSettings.showExpandedFirst = !gbSettings.showExpandedFirst}
                            active={gbSettings.showExpandedFirst}>
                            <Icon icon='segmented-control'/>
                          </PrimaryIconButton>
                        </Tooltip2>

                        <Tooltip2 content={'Compact Mode'}>
                          <PrimaryIconButton
                            onClick={() => gbSettings.compact = !gbSettings.compact}
                            active={gbSettings.compact}>
                            <Icon icon='id-number'/>
                          </PrimaryIconButton>
                        </Tooltip2>
                      </Fragment>
        // Release mode toolbar items
                      : false && <Fragment>
		  <Tooltip2 content={'Flat Mode'}>
			  <PrimaryIconButton onClick={() => gbSettings.groupView = gbSettings.groupView == 'flat' ? 'card' : 'flat'} active={gbSettings.groupView == 'flat'}><Icon icon='alignment-vertical-center'/></PrimaryIconButton>
		  </Tooltip2>

		  <Tooltip2 content={'Wrap Lines'}>
			  <PrimaryIconButton onClick={() => gbSettings.noWrap = !gbSettings.noWrap} active={!gbSettings.noWrap}><Icon icon='vertical-distribution'/></PrimaryIconButton>
		  </Tooltip2>

		  <Tooltip2 content={'Show Expanded Items First'}>
			  <PrimaryIconButton
				  onClick={() => gbSettings.showExpandedFirst = !gbSettings.showExpandedFirst}
				  active={gbSettings.showExpandedFirst}>
				  <Icon icon='segmented-control'/>
			  </PrimaryIconButton>
		  </Tooltip2>
	  </Fragment>}
	  */}

    </ToolbarDialogButtonGroup>

    {/*<ToolbarDialogButtonGroup align='right'>
      <label className={css.label}>Card Size:</label>
      <CardSizeSliderCatalog className={css.slider}/>
    </ToolbarDialogButtonGroup>*/}

    <ToolbarDialogButtonGroup align='right'>
      <Typography className={css.label} variant='h5'>{strings.imageRatio}</Typography>
      <CardImageRatioViewOptions/>
    </ToolbarDialogButtonGroup>
    {_.size(availableExports) > 0 && (
      <ToolbarDialogButtonGroup align='left'>
        <ExportOptions/>
      </ToolbarDialogButtonGroup>)
    }
  </Navbar>)
})

export const BlueprintCardInPopoverRefactorMe = styled(Card)`
  &.bp3-card {
    display: flex;
    flex-direction: column;
    padding: 0;

    > form {
      padding: 15px;

      &:not(:last-child) {
        border: solid thin var(--border-color);
      }

      fieldset {
        width: 100%;
        border: none;
        padding: 0;

        /*&:not(:last-child) {
          margin-bottom: calc(var(--theme-spacing) * 2);
        }*/

        > legend {
          padding: 8px 15px;
          font-size: 14px;
          width: 100%;
          background: var(--background-grey-3);
          font: var(--typography-body1);
          color: var(--tertiary-grey);
          text-transform: uppercase;

        }

        > *:not(legend) {
          padding: 15px;
        }

        .bp3-control.bp3-radio.bp3-inline {
          margin-bottom: 5px;
        }
      }
    }
  }
`

// region Styling

export const FormOptionsGrid = observer<{ className?: string }>(function ({className, ...props}) {
  return <div className={classNames(css.grid, className)} {...props} />
})

const FormOptionsGridFieldName = styled.div``
export const FormOptionsGridFieldText = styled.div``

export const FormOptionsGridField = styled.div.attrs(props => ({className: classNames(props.className, css.gridField), ['data-type']: 'FormOptionsGridField'}))`
  .name {

  }

`

const SliderContainer = styled.div`

`

export const ToolbarDialogButtonGroup = styled(NavbarGroup).attrs({className: css.toolbarDialogButtonGroup})`
  ${SliderContainer} {
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;

    /*&:not(:last-child) {*/
    border-right: var(--thin-border);
    padding-right: var(--theme-spacing);
    margin-right: var(--theme-spacing);
    /*}*/
  }
`

// endregion

//region Store
export class CardViewToolbarMenuPanelStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
    this.catalog.componentStores.viewOptionsToolbar = this
  }

  onPopoverOpening = () => {
    this.catalog.hoverPopoverItemId = null
    if (this.searchText) {
      setTimeout(() => this.searchText = '', 50)
    }
  }

  registerEffects = () => {
    const {catalog} = this
    const toDispose = [
      autorun(() => {
        this.cardView = _.cloneDeep(catalog.settings.cardView)
      }, {name: 'Copy catalog card view locally for performance - change the cloned version and then debounce copy it to the catalog when the user stops clicking'}),

      autorun(() => {
        // noinspection JSUnusedLocalSymbols
        const {cardView} = this

        runInAction(() => {
          this.isDirty = !_.isEqual(this.cardView, catalog.cardView)
          if (this.isDirty) {
            catalog.isShowingClientPDF ? this.updateCatalogCardView() : this._debouncedUpdateCatalogCardView()
          }
        })
      }, {name: `Update catalog card view after locally changing view options in dialog`})
    ]
    return () => {
      toDispose.forEach(d => d())
    }
  }

  @observable isDirty = false

  @observable.ref cardView: ICardViewSettings
  @observable hoveredButton: string
  @observable dragState: { draggingItem?: string, dragOverItem?: string, ignoreDragEvents?: boolean } = {}

  @computed get customCardViews() {
    const {catalog, selectableFields, selectedSectionConfig} = this,
      {catalogConfig: {cardView}, workspaceConfig: {customViews}} = catalog

    return [
      ...(selectedSectionConfig?.viewOptions?.cardViews ?? []).map(v => ({...v, section: selectedSectionConfig})),
      ...(cardView?.presets ?? []).filter(({fields}) => fields.every(f => selectableFields.find(sf => sf.field == f))),
      ...(customViews ?? []).filter(cv => cv.catalog?.fields).map((cv, i) => ({label: cv.label, fields: cv.catalog.fields, id: cv._id ?? `cv_${i}`}))
    ] as Array<PredefinedCardViewConfig>
  }

  @computed get hoveredButtonViewConfig() {
    const {hoveredButton, customCardViews} = this
    return !hoveredButton ? null
                          : customCardViews.find(cv => cv.id == hoveredButton)
  }

  @action onStartReorderFieldAnimation = () => {
    this.dragState.ignoreDragEvents = true
  }

  @action onFinishReorderFieldAnimation = () => {
    //console.log(`onFinishReorderFieldAnimation`)
    this.dragState.ignoreDragEvents = false
  }

  clone: HTMLElement

  fieldDragHandlers = (field: string) => {

    const {catalog} = this
    return {
      onDragStart: (e: React.DragEvent) => {
        // console.log(`onDragStart`, field)
        e.dataTransfer.setData('CardViewOptions/Field', field)

        e.stopPropagation()
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.dropEffect = 'move'

        const fieldNode = e.currentTarget.closest(`[data-type='FormOptionsGridField']`) as HTMLDivElement
        this.clone = fieldNode.cloneNode(true) as HTMLDivElement
        document.body.appendChild(this.clone)
        this.clone.setAttribute('data-is-drag-placeholder', 'true')
        const {width, height} = fieldNode.getBoundingClientRect()
        this.clone.style.setProperty('width', `${width}px`)
        this.clone.style.setProperty('height', `${height}px`)
        e.dataTransfer.setDragImage(this.clone, 0, 0)
        this.dragState = {draggingItem: field}
        catalog.viewOptionIsDragging = true
      },
      onDragOver: (e: React.DragEvent) => {
        const {dragState, dragState: {draggingItem, ignoreDragEvents}} = this
        e.stopPropagation()
        e.preventDefault()

        // console.log(`specItemDragHandlers.onDragEnter`, {draggingItem, field, ignoreDragEvents})

        if (draggingItem == field) {
          return
        }

        if (!ignoreDragEvents && draggingItem) {
          dragState.dragOverItem = field
        }
      },
      onDragLeave: (e: React.DragEvent) => {
        if (e.screenX === 0 && e.screenY === 0) { // escape was pressed
          this.clone?.remove()
          this.stopDraggingItem()
        }
      },
      onDragEnd: () => {
        this.clone?.remove()
        this.stopDraggingItem()
      },
      onDrop: () => {
        const {tempFields, orderedFields} = this

        // console.log(`onDrop`, field, toJS(tempFields))
        runInAction(() => {
          this.clone?.remove()
          if (tempFields) {
            catalog.settings.cardViewOrderOverride = Object.assign({}, (catalog.settings.cardViewOrderOverride ?? {}), {[catalog.perspectiveId]: orderedFields.map(f => f.field)})
            //console.log(toJS({override: catalog.settings.cardViewOrderOverride, tempCardFields, visibleCardFields}, {recurseEverything: true}))
          } else {
            console.log(`Did not handle drop due to tempCardFields being null`)
          }
          this.stopDraggingItem()
        })
      }
    }
  }

  @action stopDraggingItem = () => {
    this.dragState = {}
    this.tempFields = null
    this.catalog.viewOptionIsDragging = true
  }

  @action selectCustomCardView = (cv: PredefinedCardViewConfig) => {
    this.cardView = this.catalog.cv.selectCustomCardView(cv, this.cardView)
  }

  @computed get dynamicFieldsNoneSelected() {
    const {catalog: {sectionDynamicFields}} = this
    return Array.from(sectionDynamicFields.keys()).every(f => !this.cardView[f])
  }

  @computed get dynamicFieldsAllSelected() {
    const {catalog: {sectionDynamicFields, workspaceConfig}} = this,
      sectionConfig = workspaceConfig.sections?.filter(({id}) => id == this.selectedSection)
    // selected workspace has dynamic fields are available or not
    if (sectionConfig?.length > 0 && sectionConfig[0].viewOptions?.field)
      return Array.from(sectionDynamicFields.keys()).every(f => this.cardView[f] === true)
    else
      return true
  }

  _debouncedUpdateCatalogCardView = _.debounce(() => {
    this.updateCatalogCardView()
  }, ui.settings.updateCardViewDebounce)

  @action updateCatalogCardView = () => {
    if (this.isDirty) {
      this.isDirty = false
      // Give a moment to turn off spinner
      this._finallyUpdateTrueCatalogCardViewDebounced()
    }
  }

  private _finallyUpdateTrueCatalogCardViewDebounced = _.debounce(action(() => {
    this.catalog.settings.cardView = _.cloneDeep(this.cardView)
  }), 50)

  @computed get selectableFields(): Array<ICatalogFieldConfig> {
    const {fieldsFor, catalog, selectedSection: section, catalog: {pdfCardFields, cardFieldViewOptionsSortFunction}} = this
    const fields = _.orderBy(
      fieldsFor({section}).filter(n => !shouldIgnoreCatalogField(catalog.workspaceConfig, n.field) && !n?.card?.hide),
      f => cardFieldViewOptionsSortFunction)

    return [...fields, ...pdfCardFields]
  }

  @computed get orderedFields() {
    const {catalog, dragState, dragState: {draggingItem, dragOverItem}, tempFields, overriddenFields} = this

    if (!draggingItem) {
      if (catalog.settings.showSelectedFirst) {
        return _.sortBy(overriddenFields, f => !catalog.cardView[f.field]?.show)
      } else {
        return overriddenFields
      }
    }

    if (draggingItem && dragOverItem && draggingItem != dragOverItem) {
      const r = _.cloneDeep(tempFields ?? overriddenFields),
        fromIndex = r.findIndex(f => f.field == draggingItem),
        toIndex = r.findIndex(f => f.field == dragOverItem)

      if (fromIndex != -1 && toIndex != -1) {
        utils.array_move(r, fromIndex, toIndex)
        false && console.log(
          toJS({
            from: draggingItem,
            to: dragOverItem, fromIndex, toIndex,
            original: overriddenFields,
            lastTemp: tempFields,
            tempNew: r
          }, {recurseEverything: true}))

        this.tempFields = r
        setTimeout(() => dragState.dragOverItem = null, 0)
      }
    }

    return this.tempFields ?? overriddenFields
  }

  @observable selectedSection: string = 'all'

  @computed get selectedSectionConfig(): ISectionConfig {
    const {selectedSection, catalog} = this

    return selectedSection ? catalog.sectionById[selectedSection] : null
  }

  @action onToggleGroupSectionFieldsOnCard = () => {
    const {selectedSection, catalog} = this
    catalog.settings.groupSectionFieldsOnCard[selectedSection] = !catalog.settings.groupSectionFieldsOnCard[selectedSection]
  }

  @action selectSection = (id: string) => () => {
    this.selectedSection = id
  }

  @action onChangeSection = (e: React.ChangeEvent<HTMLSelectElement>) => this.selectedSection = e.currentTarget.value

  @computed get sectionOptions() {
    const {catalog: {strings, sections, id, catalogItemName, sectionById}, fieldsFor} = this
    return _.uniqBy([
      {value: 'all', label: _.capitalize(strings.all)},
      {value: id, label: catalogItemName},
      ...(sections ?? [])
        .filter(s => s.visible !== false && !_.isEmpty(fieldsFor({section: s.id})))
        .map(({id, label}) => ({label, value: id}))
    ], s => s.value).filter(_.identity)
  }

  fieldsFor = computedFn(({section}: { section? }): Array<ICatalogFieldConfig> => {
    const {catalog, catalog: {fieldBySection}, stores: {workspaces: {workspaceConfig}}} = this

    let r
    if (Object.keys(fieldBySection).length == 1) {
      r = _.values(fieldBySection)[0]
    } else if (section === 'all' || !section) {
      r = catalog.availableCardFields
    } else {
      r = section ? (fieldBySection[section] ?? [])
                  : fieldBySection[workspaceConfig._id]
    }

    //console.log({section, r})
    return r.filter(f => f?.card?.hide !== true)
  }, {name: 'fieldsFor'})

  countFor = (where: { section? }) => {
    const fields = this.fieldsFor(where),
      {catalog: {cv: {cardViewWithAttributes}}} = this,
      selectedFields = fields.filter(({field}) => cardViewWithAttributes[field])

    return _.isEmpty(selectedFields) ? '' : ` (${selectedFields.length})`
  }

  tempFields: Array<ICatalogFieldConfig>

  @computed get overriddenFields() {
    const {catalog, selectableFields} = this,
      {cv: {cardViewOrderOverrideLookup}} = catalog

    if (!_.isEmpty(cardViewOrderOverrideLookup)) {
      return _.orderBy(selectableFields, f => cardViewOrderOverrideLookup[f.field] ?? selectableFields.length)
    }
    return selectableFields
  }

  onToggleCardViewFieldVisible = (field: string) => () => {
    this.cardView = this.catalog.cv.toggleCardViewFieldVisible(field, this.cardView)
  }

  // region Search

  @observable searchText: string = ''
  onSearch = (e, value: string) => {
    this.searchText = value
  }

  @computed get showAutoSaveInline() {
    const {catalog: {workspaceConfig}} = this
    return workspaceConfig?.catalog?.fields?.some(f => f.editable)
  }

  isSelected = (field: string) => {
    const {cardView} = this
    return cardView?.[field]?.show === true
  }

  @computed get isSearching() {
    return !_.isEmpty(this.searchText)
  }

  @computed get searchWords() {
    const {searchText} = this
    return searchText ? _.escapeRegExp(searchText).split(' ') : []
  }

  @computed get searchResults(): Set<string> {
    const {searchText, fieldSearcher} = this
    return new Set<string>(fieldSearcher && searchText !== '' ? fieldSearcher.search(searchText).map(v => v.item.field) : [])
  }

  @computed get fieldSearcher(): Fuse<{ field, label }> {
    const {catalog, selectedSectionConfig} = this,
      {availableCardFields, sectionValues} = catalog
    return new Fuse<{ field, label }>(
      [
        ...availableCardFields.map(f => ({field: f.field, label: catalog.labelForField(f)})),
        ...(selectedSectionConfig && sectionValues.has(selectedSectionConfig.id)
            ? sectionValues.get(selectedSectionConfig.id).map(({key}) => ({field: `${selectedSectionConfig.path}.${selectedSectionConfig.viewOptions.field}.${key}`, label: key}))
            : []
        ).map(v => ({...v}))
      ],
      {
        keys: ['label'],
        isCaseSensitive: false,
        shouldSort: false,
        includeScore: true,
        threshold: .2,
        ignoreLocation: true,
        findAllMatches: true,
        useExtendedSearch: true
      })
  }

  // endregion

  @computed get sectionConfig() { return this.catalog.sectionConfigFor(this.selectedSection) }

  @computed get isSectionSelected() { return this.sectionConfig?.id == this.selectedSection }

  @computed get noneSelected() {
    const {isSectionSelected, sectionConfig, catalog} = this
    return isSectionSelected && sectionConfig?.viewOptions?.hideOnViewOptionsTab ? this.dynamicFieldsNoneSelected : catalog.cv.cardView_noneSelected(this.selectedSection, this.cardView)
  }

  @computed get allSelected() {
    const {isSectionSelected, catalog} = this
    return catalog.cv.cardView_allSelected(this.selectedSection, this.cardView) && (!isSectionSelected || this.dynamicFieldsAllSelected)
  }

  @computed get fieldsToRender() {
    return this.orderedFields
  }

  @computed get visibleFields() {
    const {fieldsToRender, isSearching, searchResults} = this

    return fieldsToRender.filter(({field}) => {
      return !ui.settings.hideFilteredSearchResults || !isSearching || searchResults.has(field)
    })
  }

  @action onClickDefaultView = () => {
    const {catalog: {cv: {cardView_resetToDefault}}, selectedSection, cardView} = this
    this.cardView = _.cloneDeep(cardView_resetToDefault(selectedSection, cardView))
  }

  @action onMouseEnterDefaultView = () => this.hoveredButton = 'default'
  @action onMouseLeaveDefaultView = () => this.hoveredButton = null

  @computed get isDefaultSelected() {
    const {selectedSection: section, cardView, catalog: {cv: {hasSection, defaultCardView, cardView_selectAll}}} = this
    //if (cardView_allSelected(section, cardView)) return false
    const array = Object.keys(defaultCardView).filter(k => hasSection({section, fieldName: k}))
    return !_.isEmpty(array) && array.every(k => defaultCardView?.[k]?.show === cardView?.[k]?.show || (defaultCardView?.[k]?.show === false && !cardView?.[k]?.show)) //Object.keys(defaultCardView).every(k => k === 'all' ? !cardView[k] : cardView[k])
  }
}

export const CardViewToolbarMenuPanelStoreContext = React.createContext<CardViewToolbarMenuPanelStore>(undefined)
export const useCardViewToolbarMenuPanelStore = () => {
  let r = useContext(CardViewToolbarMenuPanelStoreContext)

  if (!r) {
    r = useVipLocalStore((ws) => new CardViewToolbarMenuPanelStore(ws))
  }

  return r
}

//endregion

//region Components

const NoneDefaultAllButtons = observer(function PresetViewOptionButtons() {
  const store = useCardViewToolbarMenuPanelStore(),
    {strings, orderedFields, selectedSection, cardView, selectedSectionConfig, noneSelected, allSelected, catalog: {features: {}, cv: {cardView_clear, cardView_selectAll}}} = store

  return (<Fragment>
    <Button
      text={strings.none}
      minimal
      active={noneSelected}
      onClick={site.touchDevice ? undefined : () => store.cardView = cardView_clear(selectedSection, cardView)}
      onTouchEnd={site.touchDevice ? (e) => {
        e.preventDefault()
        e.stopPropagation()
        store.cardView = cardView_clear(selectedSection, cardView)
      } : undefined}/>
    {orderedFields.some(f => f?.card?.default) && !selectedSectionConfig?.viewOptions?.cardViews?.some(({id}) => id === 'default') && <DefaultViewOptionsButton/>}
    {ui.settings.viewOptions?.allowAllButton &&
	 <Button
		 text={strings.all}
		 active={allSelected}
		 minimal
		 onClick={site.touchDevice ? undefined : () => store.cardView = cardView_selectAll(selectedSection, cardView)}
		 onTouchEnd={site.touchDevice ? (e) => {
           e.preventDefault()
           e.stopPropagation()
           store.cardView = cardView_selectAll(selectedSection, cardView)
         } : undefined}
	 />}
  </Fragment>)
})

export const CustomCardViewPresetMenuItems = observer(() => {
  const store = useCardViewToolbarMenuPanelStore(),
    catalog = useCatalog(),
    customCardViews = store?.customCardViews ?? catalog.cv.customCardViews,
    selectCustomCardView = store?.selectCustomCardView ?? catalog.cv.selectCustomCardView,
    {cv: {cardView_isCustomViewSelected}} = catalog,
    cardView = store?.cardView ?? catalog.cardView

  return (
    <Fragment>
      {(customCardViews ?? []).map(cv =>
        <BPMenuItem
          text={cv.label ?? cv.id}
          shouldDismissPopover={false}
          icon={cardView_isCustomViewSelected(cv, cardView) ? 'tick' : 'blank'}
          onClick={e => selectCustomCardView(cv)}
          key={cv.id}
          onMouseEnter={store ? () => store.hoveredButton = cv.id : undefined}
          onMouseLeave={store ? () => store.hoveredButton = null : undefined}/>)}
    </Fragment>
  )
})

const ExportOptions = observer(function ExportOptions() {
  const {export: {onClickExport, availableExports}, catalog: {itemCount}} = useStores(),
    shouldDisable = itemCount === 0

  return <Popover2
    interactionKind={PopoverInteractionKind.CLICK}
    position='bottom-right'
    minimal
    usePortal
    disabled={shouldDisable}
    content={
      <Menu>
        {_.map(availableExports, (ex, index) =>
          (<BPMenuItem
            key={index}
            disabled={!ex.enabled}
            text={ex?.modal?.modalTitle}
            shouldDismissPopover={true}
            onClick={() => onClickExport(ex)}
          />)
        )}
      </Menu>
    }>
    <Button
      rightIcon={'caret-down'}
      minimal
      icon='import'
      disabled={shouldDisable}
    />
  </Popover2>
})

const FieldOptions = observer(function FieldOptions() {
  const store = useCardViewToolbarMenuPanelStore(),
    catalog = useCatalog(),
    sectionConfig = catalog.sectionConfigFor(store.selectedSection)

  // Don't show secondary attributes on tab, only show secondaries themselves
  if (sectionConfig?.viewOptions?.field && sectionConfig?.viewOptions?.hideOnViewOptionsTab) return null

  const {
      strings, fieldsToRender, visibleFields, hoveredButton,
      isSearching
    } = store,
    noResultsFound = _.isEmpty(visibleFields)

  return (
    !fieldsToRender?.length ? null :
    <div className={css.grid}
      // onStartAll={onStartReorderFieldAnimation} onFinishAll={onFinishReorderFieldAnimation}
      // disableAllAnimations
         data-hovered-preset={hoveredButton ?? undefined}
         data-hide-filtered-view-options={ui.settings.hideFilteredSearchResults}
         data-searching={isSearching}
         data-no-results={noResultsFound}
    >
      {noResultsFound && !hoveredButton && <div className={css.noFieldsResult}>{strings.noResultsFoundFor(store.searchText)}</div>}
      {fieldsToRender.map((f, i) => <CardViewOptionsToolbarMenuField key={i} field={f}/>)}
    </div>
  )
})

const CardViewOptionsToolbarMenuField = observer<{ field: ICatalogFieldConfig }>(function CardViewOptionsToolbarMenuField({field}) {
  const store = useCardViewToolbarMenuPanelStore(),
    {
      selectedSection, fieldDragHandlers, hoveredButton, hoveredButtonViewConfig,
      onToggleCardViewFieldVisible, searchResults, searchWords, isSelected, catalog,
      catalog: {settings: {showSelectedFirst}}
    } = store

  const {field: fieldName, section} = field, label = catalog.labelForField(field),
    {onDragStart, ...dragHandlers} = fieldDragHandlers(fieldName),
    inMainSection = selectedSection === 'all' || !catalog.hasViewOptionSections,
    sectionConfig = catalog.sectionConfigFor(section),
    isSearchResult = searchResults.has(fieldName)

  // YFTS-7851
  //if (ui.settings.hideFilteredViewOptions && !isSearchResult && isSearching) return null

  return (
    <CardViewToolbarMenuPanelStoreContext.Provider value={store}>
      <FormOptionsGridField as={MenuItem} key={fieldName}
                            data-field={fieldName}
                            data-search-result={isSearchResult}
                            data-highlight={!hoveredButton ? searchResults.has(fieldName) : (hoveredButton === 'default' && field.card?.default === true) || _.includes(hoveredButtonViewConfig?.fields, fieldName)}
                            {...(!inMainSection ? {} : dragHandlers)}
                            onClick={onToggleCardViewFieldVisible(fieldName)}>
        <Checkbox className={css.checkbox} checked={isSelected(fieldName)}/>
        <FormOptionsGridFieldText as={ListItemText} className={css.itemLabel}><Highlighter className={css.highlighter} textToHighlight={label} searchWords={searchWords}/></FormOptionsGridFieldText>
        {sectionConfig && <Chip className={classNames(css.sectionLabel)} label={catalog.labelForSection(sectionConfig.id, field)}/>}
        {inMainSection &&
         <Tooltip2 disabled={!showSelectedFirst}
                   content={`'Custom Sort' disabled while 'Show Selected First' is on`}>
		 <div className={css.dragHandle}
		      draggable={!showSelectedFirst}
          data-disabled={showSelectedFirst}
		      onDragStart={onDragStart}
		 >
			 <Icon icon={'drag-handle-vertical'}/>
		 </div>
         </Tooltip2>
        }

      </FormOptionsGridField>
    </CardViewToolbarMenuPanelStoreContext.Provider>
  )
})
const SelectedSectionFieldOptions = observer(function SelectedSectionFieldOptions() {
  const {catalog: {sectionValues}, selectedSectionConfig, isSelected, onToggleCardViewFieldVisible, searchResults, searchText, hoveredButton, hoveredButtonViewConfig, searchWords} = useCardViewToolbarMenuPanelStore()

  //console.log({selectedSectionConfig, dynamicFields, loading, error})

  if (!selectedSectionConfig || !sectionValues.has(selectedSectionConfig.id)) return null

  const path = `${selectedSectionConfig.path}.${selectedSectionConfig.viewOptions.field}`

  return (
    <div className={classNames(css.grid, css.borderBottom)} data-hovered-preset={hoveredButton} data-searching={!_.isEmpty(searchText)}>
      {sectionValues.get(selectedSectionConfig.id).map(({key}, i) => {
        const field = `${path}.${key}`

        return (
          <FormOptionsGridField as={MenuItem} key={i}
                                onClick={onToggleCardViewFieldVisible(field)}
                                data-key={key}
                                data-search-result={searchResults.has(field)}
                                data-highlight={!hoveredButton ? searchResults.has(field) : hoveredButtonViewConfig ? _.includes(hoveredButtonViewConfig.dynamicValues, key) : false}
          >
            <Checkbox className={css.checkbox} checked={isSelected(field)}/>
            <FormOptionsGridFieldText as={ListItemText} className={css.itemLabel}>{<Highlighter className={css.highlighter} textToHighlight={key} searchWords={searchWords}/>}</FormOptionsGridFieldText>

          </FormOptionsGridField>
        )
      })}
    </div>
  )
})

const CardImageRatioViewOptions = observer(function CardImageRatioViewOptions() {
  // optionList={imageRatioType}
  //         selectedOption={imageRatio imageRatioType.find(ir => ir.id === imageRatio)?.label}
  //         onClick={cardView_setImageRatio}
  //         isIR={true}

  const catalog = useCatalog(),
    {imageRatio} = catalog

  return (
    <Popover2
      usePortal={false}
      key={'menu'}
      minimal
      captureDismiss
      position='bottom-left'
      //className={/*!isIR && css.cardSizeDropDownMenu*/}
      content={
        <Menu>
          <CardImageRatioMenuItems/>
        </Menu>
      }>
      <Button rightIcon='caret-down'
              minimal
              className={css.clearMenuItem}
              text={imageRatio?.label ?? '!!!No Image Ratio!!!'}/>
    </Popover2>
  )
})

export const CardImageRatioMenuItems = observer(function CardImageRatioMenuItems() {
  const {catalog: {imageRatios, imageRatio: selected, settings}} = useStores()

  return <Fragment>
    {imageRatios.map(({id, label}) => {
      const dropDownText = label, // : item === 1 ? `${item} (Default)` : item,
        dropDownTextId = id // isIR ? item.id : item

      return <BPMenuItem key={dropDownTextId}
                         className={css.clearMenuItem}
                         shouldDismissPopover={false}
                         text={dropDownText}
                         icon={id === selected.id ? 'tick' : 'blank'}
                         onClick={() => settings.imageRatio = id}
      />
    })}
  </Fragment>
})

const DefaultViewOptionsButton = observer(function DefaultViewOptionsButton() {
  const store = useCardViewToolbarMenuPanelStore(),
    {strings, isDefaultSelected, onClickDefaultView, onMouseEnterDefaultView, onMouseLeaveDefaultView} = store

  return <Button
    text={strings.default}
    minimal
    active={isDefaultSelected}
    onClick={onClickDefaultView}
    onMouseEnter={onMouseEnterDefaultView}
    onMouseLeave={onMouseLeaveDefaultView}
  />
})

//endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/topBar/components/CardViewOptionsToolbarMenu.tsx